import React, { useEffect, useState } from "react";
import { Copy, Loader2, RotateCcw, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Row } from "../_ui";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");
const inputCls = "admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono";

function Snippet({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* blocked */ } };
  return (
    <div className="flex items-center gap-2">
      <code data-testid="relay-pixel-snippet" className="flex-1 text-[11px] mono text-amber-200 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 break-all">{text}</code>
      <button data-testid="relay-pixel-copy" onClick={copy} className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-2.5 py-1.5 rounded-sm text-xs font-semibold shrink-0">
        <Copy size={12} /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function RelayStatus({ status, onTest, onReset, busy, testResult }) {
  const ok = status.errors === 0;
  return (
    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40 space-y-2 text-xs" data-testid="relay-status">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mono">
        <span className={status.configured ? "text-emerald-400" : "text-amber-300"} data-testid="relay-configured">{status.configured ? "● relaying" : "○ no destination — pixel answers, nothing is reported"}</span>
        <span className="text-zinc-300" data-testid="relay-forwarded">forwarded <b>{status.forwarded.toLocaleString()}</b></span>
        <span className={ok ? "text-zinc-500" : "text-rose-400"} data-testid="relay-errors">errors <b>{status.errors.toLocaleString()}</b></span>
        <span className="text-zinc-500" data-testid="relay-last">last {fmt(status.last_forwarded_at)}{status.last_status ? ` · HTTP ${status.last_status}` : ""}</span>
      </div>
      {status.last_error && <div className="text-rose-400 mono break-all" data-testid="relay-last-error">last error: {status.last_error} ({fmt(status.last_error_at)})</div>}
      {testResult && (
        <div data-testid="relay-test-result" className={`mono break-all ${testResult.ok ? (testResult.status >= 400 ? "text-amber-300" : "text-emerald-400") : "text-rose-400"}`}>
          {testResult.ok ? "✓ destination reached" : "✕ unreachable"} → {testResult.url} {testResult.status ? `HTTP ${testResult.status}` : ""} {testResult.error || ""} · {testResult.ms} ms
          {testResult.ok && testResult.status >= 400 && <span className="text-zinc-500"> (it rejected the test parameters — real pixel hits carry your campaign IDs)</span>}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button data-testid="relay-test-btn" onClick={onTest} disabled={busy || !status.configured} className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-2.5 py-1.5 rounded-sm text-xs font-semibold">
          {busy ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Send test ping
        </button>
        <button data-testid="relay-reset-btn" onClick={onReset} disabled={busy || (!status.forwarded && !status.errors)} className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 disabled:opacity-40 px-2.5 py-1.5 rounded-sm text-xs font-semibold">
          <RotateCcw size={12} /> Reset counters
        </button>
      </div>
    </div>
  );
}

/** Settings → Email-open pixel relay: replaces the operator's open.php (GIF + forward query to the mailer). */
export default function TrackingRelayPanel({ site, form, setField }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const load = async () => { try { setStatus((await api.get(`/sites/${site.id}/tracking`)).data); } catch { /* keep previous */ } };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id, site.tracking_forward_url]);
  async function test() {
    setBusy(true); setTestResult(null);
    try { const { data } = await api.post(`/sites/${site.id}/tracking/test`); setTestResult(data); setStatus(data.status_after); }
    catch (e) { setTestResult({ ok: false, url: "", error: e?.response?.data?.detail || "Test failed", ms: 0 }); }
    finally { setBusy(false); }
  }
  async function reset() {
    setBusy(true);
    try { await api.post(`/sites/${site.id}/tracking/reset`); setTestResult(null); await load(); } finally { setBusy(false); }
  }
  const dirty = (form.tracking_forward_url || "") !== (site.tracking_forward_url || "");
  return (
    <div className="space-y-3" data-testid="tracking-relay-panel">
      <div className="text-xs text-zinc-400">
        PHP can&apos;t run here, so <b className="text-zinc-200">every <span className="mono">/*.php</span> URL on this domain</b> behaves like your <span className="mono">open.php</span>: it answers a 1×1 GIF instantly and forwards the query string (<span className="mono">GET destination?…</span>, 2 s timeout, errors ignored) to your mailing software. Keep your email tags as they are.
      </div>
      <Row label="Forward to (mailing software hook)">
        <input data-testid="relay-forward-url" value={form.tracking_forward_url} onChange={(e) => setField("tracking_forward_url", e.target.value)}
               placeholder="https://node.example.com/email/google/open" className={inputCls} />
        <div className="text-[11px] text-zinc-500 mt-1">https only, no query string — we append <span className="mono">?{"{"}query{"}"}</span> from the pixel URL.{dirty && <span className="text-amber-300"> Unsaved — click Save Settings.</span>}</div>
      </Row>
      {status && <Snippet text={status.pixel_snippet} />}
      {status && <RelayStatus status={status} onTest={test} onReset={reset} busy={busy} testResult={testResult} />}
    </div>
  );
}
